import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import ReactDatePicker, { registerLocale } from 'react-datepicker'
import classnames from 'classnames'
import uniqid from 'uniqid'
import styled from '@emotion/styled'

import { InputText, INPUT_TEXT_VARIANT_DANGER } from '../InputText/InputText'

import de from 'date-fns/locale/de'
import en from 'date-fns/locale/en-US'
import es from 'date-fns/locale/es'
import fr from 'date-fns/locale/fr'
import id from 'date-fns/locale/id'
import it from 'date-fns/locale/it'
import ja from 'date-fns/locale/ja'
import pt from 'date-fns/locale/pt'
import zh from 'date-fns/locale/zh-CN'

import 'react-datepicker/dist/react-datepicker.css'
import './DatePicker.scss'

registerLocale('da', en)
registerLocale('de', de)
registerLocale('en', en)
registerLocale('es', es)
registerLocale('fr', fr)
registerLocale('id', id)
registerLocale('it', it)
registerLocale('ja', ja)
registerLocale('pt', pt)
registerLocale('zh', zh)

const StyledDatePicker = styled.div`
  .react-datepicker-popper {
    z-index: ${({ zIndex }) => zIndex || null};
  }
`

export const DatePicker = ({
  callback,
  className,
  dateFormat,
  disabled,
  id,
  invalid,
  isClearable,
  maxDate,
  minDate,
  name,
  placeholder,
  value,
  locale,
  tabIndex,
  zIndex,
  'data-mu-test': dataAttributeTest,
  'data-tip': dataAttributeTip
}) => {
  const [open, setOpen] = useState(false)
  const inputRef = useRef(null)
  const { current: uuid } = useRef(uniqid('datepicker-'))

  const handleCalendarClose = () => {
    setOpen(false)
  }
  const handleCalendarOpen = () => {
    setOpen(true)
  }

  return (
    <StyledDatePicker
      className={classnames('c-datepicker', className, {
        'c-datepicker--error': invalid,
        'c-datepicker--disabled': disabled,
        'c-datepicker--active': open && !disabled
      })}
      data-mu-test={classnames('datepicker', dataAttributeTest)}
      data-tip={dataAttributeTip}
      zIndex={zIndex}
    >
      <ReactDatePicker
        selected={value}
        onChange={callback}
        customInput={
          <DatePickerInput
            ref={inputRef}
            key={uuid}
            value={value}
            placeholder={placeholder}
            variant={invalid ? INPUT_TEXT_VARIANT_DANGER : undefined}
            disabled={disabled}
            id={id}
            name={name}
            tabindex={tabIndex}
          />
        }
        className='c-datepicker__input'
        disabled={disabled}
        isClearable={isClearable}
        dateFormat={dateFormat}
        placeholderText={placeholder}
        minDate={minDate}
        maxDate={maxDate}
        autoComplete='off'
        onCalendarClose={handleCalendarClose}
        onCalendarOpen={handleCalendarOpen}
        locale={locale}
      />
    </StyledDatePicker>
  )
}

/* Please see: https://github.com/Hacker0x01/react-datepicker/issues/862 */
const DatePickerInput = React.forwardRef(
  ({ key, tabindex, ...props }, _ref) => {
    return (
      <span ref={_ref} key={key}>
        <InputText {...props} tabIndex={tabindex} />
      </span>
    )
  }
)

DatePicker.defaultProps = {
  id: null,
  value: null,
  disabled: false,
  locale: 'en',
  isClearable: true,
  isError: false,
  dateFormat: 'yyyy-MM-dd',
  onBlur: null,
  tabIndex: -1
}

DatePicker.propTypes = {
  callback: PropTypes.func,
  className: PropTypes.string,
  dateFormat: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  invalid: PropTypes.bool,
  isClearable: PropTypes.bool,
  maxDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  name: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  locale: PropTypes.string,
  tabIndex: PropTypes.number,
  zIndex: PropTypes.number,
  /**
   * Additional values for data-attribute used for tests and tracking
   **/
  'data-mu-test': PropTypes.string,
  'data-tip': PropTypes.string
}
